import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container, Sprite } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
import { AVATAR_RENDER_SCALE_FACTOR, AVATAR_SHADOW_ALPHA, AVATAR_SHADOW_OFFSET_X_RATIO, AVATAR_SHADOW_OFFSET_Y_RATIO, AVATAR_TEXTURE_SIZE } from '../../../../constants/avatarConstants';
import { thunkMaybeTriggerSitPlayerAnimation } from '../../../../redux/actions/animationsActions';
import { useAppDispatch } from '../../../../redux/store';
import { ALL_UNIQUE_AVATAR_PROPERTIES } from '../../../../types/uniqueAvatarTypes';
import { getServiceAvatarUrl } from '../../../../utils/avatarServiceUtils';
import { fetchToImageBitmap } from '../../../../utils/imageUtils';
import { assertUnreachable } from '../../../../utils/tsUtils';
import { isTestEnvironment } from '../../../../utils/utils';
import { LoadingSeatAnimation } from '../../pixiComponents/loadingSeatAnimation';
import { ActionPlayerFilterContainer } from '../actionPlayerFilterContainer';
function getPixiData(avatar, seatIndex) {
    if (!isTestEnvironment()) {
        return null;
    }
    switch (avatar.type) {
        case 'custom': {
            const pixiDataColorsFields = Object.keys(avatar.avatar.properties.colors).reduce((acc, curr) => {
                acc[`${curr}Color`] = avatar.avatar.properties.colors[curr];
                return acc;
            }, {});
            const pixiDataPartsFields = Object.keys(avatar.avatar.properties.parts).reduce((acc, curr) => {
                const avatarPart = avatar.avatar.properties.parts[curr];
                acc[curr] = avatarPart.subType ? { index: avatarPart.index, subType: avatarPart.subType } : avatarPart.index;
                return acc;
            }, {});
            return {
                type: 'custom-avatar',
                seatIndex,
                ...pixiDataColorsFields,
                ...pixiDataPartsFields
            };
        }
        case 'unique': {
            return {
                type: 'unique-avatar',
                seatIndex,
                imageUrl: ALL_UNIQUE_AVATAR_PROPERTIES[avatar.uniqueAvatarType].url
            };
        }
        case 'legacy': {
            return {
                type: 'legacy-avatar',
                seatIndex,
                legacyAvatarNumber: avatar.faceNum
            };
        }
        default: {
            assertUnreachable(avatar);
        }
    }
}
const AvatarDisplay = forwardRef(({ avatar, seatIndex, baseAvatarScaleFactor }, ref) => {
    const isMounted = useMountedState();
    const [loadedImageBitmaps, setLoadedImageBitmaps] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const isLoadingRef = useRef(isLoading);
    const dispatch = useAppDispatch();
    const avatarShadowRef = useRef(null);
    const latestRenderOperationId = useRef(0);
    useImperativeHandle(ref, () => ({ avatarShadowRef, isLoadingRef }), []);
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);
    useEffect(() => {
        async function effect() {
            setIsLoading(true);
            latestRenderOperationId.current = (latestRenderOperationId.current + 1) % 10000000;
            const currRenderOperationId = latestRenderOperationId.current;
            const [avatarImageBitmap, shadowImageBitmap] = await Promise.all([
                fetchToImageBitmap(getServiceAvatarUrl({ avatar, type: 'avatar', size: AVATAR_TEXTURE_SIZE, trimTransparentPixels: false })),
                fetchToImageBitmap(getServiceAvatarUrl({ avatar, type: 'shadow', size: AVATAR_TEXTURE_SIZE, trimTransparentPixels: false }))
            ]);
            if (currRenderOperationId !== latestRenderOperationId.current) {
                // operation is old, a more recent render operation is likely in progress, so ignore these results
                return;
            }
            if (!avatarImageBitmap || !shadowImageBitmap) {
                return;
            }
            if (!isMounted()) {
                return;
            }
            setLoadedImageBitmaps({ avatarImageBitmap, shadowImageBitmap });
            setIsLoading(false);
            // Now that the avatar is fully loaded, show the sit animation if it is pending.
            // The sit animation should not be pending if the player had already sat down, but just made a change to the avatar.
            dispatch(thunkMaybeTriggerSitPlayerAnimation(seatIndex));
        }
        effect();
    }, [avatar, dispatch, isMounted, seatIndex]);
    let avatarContainer = null;
    if (loadedImageBitmaps) {
        const { avatarImageBitmap, shadowImageBitmap } = loadedImageBitmaps;
        const scale = AVATAR_RENDER_SCALE_FACTOR * baseAvatarScaleFactor;
        const x = Math.round(-AVATAR_TEXTURE_SIZE / 2);
        const y = Math.round(-AVATAR_TEXTURE_SIZE / 2);
        const shadowX = x + AVATAR_TEXTURE_SIZE * AVATAR_SHADOW_OFFSET_X_RATIO;
        const shadowY = y + AVATAR_TEXTURE_SIZE * AVATAR_SHADOW_OFFSET_Y_RATIO;
        avatarContainer = (_jsxs(Container, { scale: scale, "pixi-data": getPixiData(avatar, seatIndex), children: [_jsx(Sprite, { ref: avatarShadowRef, texture: PIXI.Texture.from(shadowImageBitmap), x: shadowX, y: shadowY, alpha: AVATAR_SHADOW_ALPHA }), _jsx(ActionPlayerFilterContainer, { seatIndex: seatIndex, children: _jsx(Sprite, { texture: PIXI.Texture.from(avatarImageBitmap), x: x, y: y }) })] }));
    }
    return (_jsxs(Container, { x: 0, y: 0, children: [avatarContainer, isLoading && _jsx(LoadingSeatAnimation, { seatIndex: seatIndex })] }));
});
AvatarDisplay.displayName = 'AvatarDisplay';
export { AvatarDisplay };
